/**
 * @file routes.ts
 * @description Типизированный реестр всех API-маршрутов, доступных через глобальный объект `routable`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ## Как работает система маршрутизации в проекте
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Проект — монолит на PHP/Symfony + React. Маршруты генерируются **на сервере** в
 * Twig-шаблонах через Symfony `path()` и прокидываются в браузер при загрузке страницы:
 *
 * ```twig
 * {# templates/base.html.twig #}
 * routable.addRoutes({
 *   'companyFinanceExportRequestsList':
 *       '{{ path("api_finance_export_requests_list", {'company': user.company.id}) }}',
 *
 *   'financeExportRequestDelete':
 *       '{{ path('api_finance_export_request_delete', {
 *           'company': user.company.id,
 *           'requestId': '_requestId_'
 *       }) }}',
 * });
 * ```
 *
 * В итоге `routable` — это простой JavaScript-объект `{ key → url }`. При загрузке страницы
 * PHP уже подставил реальные значения (ID компании, ID пользователя и т.п.), а `_param_` —
 * это клиентские плейсхолдеры, которые заменяются через `helpable.replaceUrlParameters`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ## Как использовать этот файл
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ### Простой маршрут (без параметров)
 * ```typescript
 * import { routes } from '@shared/api/routes';
 *
 * // URL уже содержит baked-in company ID из Twig — просто вызываем как функцию
 * $api.get(routes.companyFinanceExportRequestsList());
 * $api.post(routes.financeExportRequestsCheckStatus(), formData);
 * ```
 *
 * ### Маршрут с параметрами
 * ```typescript
 * import { routes } from '@shared/api/routes';
 *
 * // TypeScript проверит наличие и тип всех плейсхолдеров
 * $api.delete(routes.financeExportRequestDelete({ _requestId_: 42 }));
 * $api.get(routes.getWidgetPlanData({ _widgetId_: widgetId }));
 * ```
 *
 * ### Миграция от прямого использования routable
 * ```typescript
 * // ДО (разбросано по api-funcs.ts):
 * // @ts-ignore
 * const url = routable.getRoute('financeExportRequestDelete');
 * // @ts-ignore
 * const finalUrl = helpable.replaceUrlParameters(url, { _requestId_: id });
 *
 * // ПОСЛЕ (этот файл):
 * import { routes } from '@shared/api/routes';
 * const finalUrl = routes.financeExportRequestDelete({ _requestId_: id });
 * ```
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ## Обоснование: зачем это нужно
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ### 1. Единая точка входа — нет рассыпанных строк-ключей
 * Без этого файла каждый `api-funcs.ts` содержит `routable.getRoute('someRoute')` вразброс.
 * Опечатка в строковом ключе — молчаливый 404 в рантайме и нет ошибки от TS.
 * Здесь — ошибка компиляции сразу при написании кода.
 *
 * ### 2. Типизация параметров исключает ошибки плейсхолдеров
 * `helpable.replaceUrlParameters(url, { _requestId_: id })` — любая опечатка в имени
 * плейсхолдера проходит незамеченной, URL остаётся сломанным. Типизированные функции
 * делают параметры явными и проверяемыми компилятором.
 *
 * ### 3. Документация «живая» и актуальная
 * Этот файл — исчерпывающий список всех API-эндпоинтов, которые используются в React.
 * Легко найти все места использования конкретного маршрута через «Find Usages» в IDE.
 * При добавлении нового маршрута в Twig он добавляется здесь — один раз, явно.
 *
 * ### 4. Тестируемость
 * Мок всего API в тестах = мок одного модуля `@shared/api/routes`:
 * ```typescript
 * jest.mock('@shared/api/routes', () => ({
 *   routes: { financeExportRequestDelete: jest.fn(() => '/mock/url') },
 * }));
 * ```
 *
 * ### 5. Подготовка к разделению фронт/бэк
 * Когда придёт время вынести React в отдельное SPA, достаточно поменять реализацию
 * `getRoute` в одном месте (например, на константы или env-переменные) — все вызывающие
 * стороны останутся без изменений. Это прямой путь к полноценному API-слою.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ## Откуда берутся маршруты
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Здесь собраны маршруты из `templates/base.html.twig` — они доступны **глобально**
 * на всех страницах. Страничные маршруты (из estimate/view.html.twig, payment.html.twig
 * и т.п.) следует добавлять в соответствующие feature-модули, а не сюда, так как они
 * доступны только на конкретных страницах.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Заменяет плейсхолдеры вида `_param_` в URL на переданные значения.
 *
 * Типизированный аналог `helpable.replaceUrlParameters`, без зависимости от глобала.
 *
 * @example
 * buildUrl('/company/42/request/_requestId_/delete', { _requestId_: 99 })
 * // => '/company/42/request/99/delete'
 */
export const buildUrl = (url: string, params: Record<string, string | number>): string => {
    if (!url) {
        throw new Error('[routes] buildUrl received an empty url');
    }

    return Object.entries(params).reduce(
        (result, [key, value]) => result.replace(key, String(value)),
        url
    );
};

/**
 * Получает URL маршрута из глобального объекта `routable`.
 * Company ID и другие серверные параметры уже вшиты Twig'ом при загрузке страницы.
 *
 * Вызывается лениво (внутри функций `routes`), чтобы не упасть при импорте модуля
 * до инициализации `routable`.
 */
const getRoute = (key: string): string => {
    // @ts-expect-error routable is provided globally by Twig.
    const route = routable.getRoute(key);
    if (!route) {
        throw new Error(`[routes] Route "${key}" is not defined in routable payload`);
    }

    return route;
};
// ─────────────────────────────────────────────────────────────────────────────
// Routes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Типизированные маршруты приложения.
 *
 * - Простые маршруты: `() => string` — URL полностью готов, серверные параметры вшиты
 * - Параметризованные: `(params) => string` — содержат `_param_` плейсхолдеры
 *
 * Источник: `templates/base.html.twig`, блок `routable.addRoutes({...})`
 */
export const routes = {
    // ─── AI assistant ───────────────────────────────────────────────────────

    /** POST Получить ответ ассистента по публичной базе знаний */
    knowledgeAssistantMessage: () => getRoute('knowledgeAssistantMessage'),

    getTaskBoardViewState: () => getRoute('getTaskBoardViewState'),
    updateTaskBoardViewState: () => getRoute('updateTaskBoardViewState'),
    getCustomTaskComments: (params: { _customTask_: string | number }) =>
        buildUrl(getRoute('getCustomTaskComments'), params),
    pollCustomTaskComments: (params: { _customTask_: string | number }) =>
        buildUrl(getRoute('pollCustomTaskComments'), params),
    createCustomTaskComment: (params: { _customTask_: string | number }) =>
        buildUrl(getRoute('createCustomTaskComment'), params),
    deleteCustomTaskComment: (params: { _customTask_: string | number; _comment_: string | number }) =>
        buildUrl(getRoute('deleteCustomTaskComment'), params),
    // ─── Sidebar & Navigation ────────────────────────────────────────────────

    /** GET Основная боковая панель */
    getSidebar: () => getRoute('getSidebar'),

    /** GET Список проектов в боковой панели (categoryId вшит Twig'ом в URL) */
    getObjectList: () => getRoute('getObjectList'),

    /**
     * GET Список смет в боковой панели
     * @param _archive_ - '0' | '1' — показывать ли архивные сметы
     */
    getEstimateList: (params: { _archive_: string | number }) =>
        buildUrl(getRoute('getEstimateList'), params),

    /**
     * GET Поиск по боковой панели
     * @param _search_ — строка поиска
     */
    getSearchSidebar: (params: { _search_: string }) =>
        buildUrl(getRoute('getSearchSidebar'), params),

    /**
     * POST Обновить состояние свёрнутости боковой панели
     * @param _value_ — '1' свёрнута, '0' развёрнута
     */
    updateIsCollapsedSidebar: (params: { _value_: string | number }) =>
        buildUrl(getRoute('updateIsCollapsedSidebar'), params),

    /** POST Обновить боковую панель (доступен только авторизованным пользователям) */
    refreshSidebar: () => getRoute('refreshSidebar'),

    // ─── User & Profile ──────────────────────────────────────────────────────

    /** GET Страница профиля пользователя */
    viewProfile: () => getRoute('viewProfile'),

    /** GET/PATCH/DELETE Данные текущего профиля */
    profile: () => '/web-api/profile',

    /** GET Заказы текущего пользователя */
    profileOrders: () => '/web-api/profile/orders',

    /** POST Аватар текущего пользователя */
    profileAvatar: () => '/web-api/profile/avatar',

    /** PATCH Пароль текущего пользователя */
    profilePassword: () => '/web-api/profile/password',

    /** PATCH Настройка системных подсказок */
    profilePreferences: () => '/web-api/profile/preferences',

    /** PATCH Подписки на уведомления */
    profileNotificationSubscriptions: () => '/web-api/profile/notification-subscriptions',

    /** POST Создать API-токен */
    profileApiToken: () => '/web-api/profile/api-token',

    /** POST Перевыпустить API-токен */
    profileApiTokenRefresh: () => '/web-api/profile/api-token/refresh',

    /** Публичная страница условий реферальной программы */
    profileReferralProgram: () => 'https://smetter.ru/referral',

    /** Документация публичного API */
    profilePublicApiDocs: () => '/doc/q4mU91-Enq17-50MpQ-Z-zqe/public_api_v2',

    /** GET Попап оплаты подписки со страницы профиля */
    profileChangeTariffPopup: () =>
        `${buildUrl(getRoute('openChangeTariffPopup'), { _prolong_: 1 })}&changeManual=1`,

    /** GET Главный экран активной компании */
    homeScreen: () => getRoute('homeScreen'),

    /** POST Сохранить статус справочного центра */
    rememberHelpCenterUrl: () => getRoute('rememberHelpCenterUrl'),

    /** POST Активировать пользователя (только для неактивных аккаунтов) */
    activateUser: () => getRoute('activateUser'),

    /** POST Войти через десктопную веб-версию */
    loginFromDesktop: () => getRoute('loginFromDesktop'),

    // ─── Company & Employees ─────────────────────────────────────────────────

    /** GET Список сотрудников для экрана «Моя команда» */
    getMyTeam: () => getRoute('getMyTeam'),

    /** GET Список сотрудников компании (для выбора/назначения) */
    getEmployeeList: () => getRoute('getEmployeeList'),

    /** GET Поиск сотрудников по компании */
    findEmployeeList: () => getRoute('findEmployeeList'),

    /** POST Добавить сотрудника в компанию */
    addEmployee: () => getRoute('addEmployee'),

    /**
     * DELETE Удалить сотрудника из компании
     * @param _employeeId_ — ID сотрудника
     */
    removeEmployee: (params: { _employeeId_: number | string }) =>
        buildUrl(getRoute('removeEmployee'), params),

    /**
     * GET Получить доступы сотрудника
     * @param _employeeId_ — ID сотрудника
     */
    getEmployeeAccesses: (params: { _employeeId_: number | string }) =>
        buildUrl(getRoute('getEmployeeAccesses'), params),

    /**
     * POST Обновить доступы сотрудника
     * @param _employeeId_ — ID сотрудника
     */
    updateEmployeeAccesses: (params: { _employeeId_: number | string }) =>
        buildUrl(getRoute('updateEmployeeAccesses'), params),

    /**
     * POST Добавить сотрудника к существующим сметам компании
     * @param _email_ — email сотрудника
     */
    addEmployeeToExistEstimates: (params: { _email_: string }) =>
        buildUrl(getRoute('addEmployeeToExistEstimates'), params),

    /** GET Список юридических лиц компании */
    getLegalList: () => getRoute('getLegalList'),

    /** GET Список категорий для создания проекта */
    categoryListForProject: () => getRoute('categoryListForProject'),

    // ─── Projects ────────────────────────────────────────────────────────────

    /** POST Создать проект */
    projectCreate: () => getRoute('projectCreate'),

    /** GET Количество проектов в компании */
    getProjectsCount: () => getRoute('getProjectsCount'),

    /** GET Проверка возможности добавить новую смету */
    checkAbilityToAddEstimate: () => getRoute('checkAbilityToAddEstimate'),

    /** GET Состояние подсказок онбординга */
    onboardingHintsState: () => getRoute('onboardingHintsState'),

    /** POST Отметить подсказку онбординга завершенной */
    completeOnboardingHint: () => getRoute('completeOnboardingHint'),

    /**
     * POST Добавить пользователя в проект
     * @param _project_ — ID проекта
     */
    addProjectUser: (params: { _project_: number | string }) =>
        buildUrl(getRoute('addProjectUser'), params),

    /**
     * DELETE Удалить пользователя из проекта
     * @param _project_ — ID проекта
     * @param _projectUserId_ — ID записи ProjectUser
     */
    deleteProjectUser: (params: { _project_: number | string; _projectUserId_: number | string }) =>
        buildUrl(getRoute('deleteProjectUser'), params),

    // ─── Finance Export ───────────────────────────────────────────────────────

    /**
     * GET Список заявок на экспорт финансов.
     */
    companyFinanceExportRequestsList: () => getRoute('companyFinanceExportRequestsList'),

    /**
     * POST Проверить статус заявок на экспорт.
     * Принимает FormData с полем `ids[]` — массив ID заявок.
     * Используется в `useCheckFinanceExportReportsStatus` для поллинга.
     */
    financeExportRequestsCheckStatus: () => getRoute('financeExportRequestsCheckStatus'),

    /**
     * DELETE Удалить заявку на экспорт
     * @param _requestId_ — ID заявки
     */
    financeExportRequestDelete: (params: { _requestId_: number | string }) =>
        buildUrl(getRoute('financeExportRequestDelete'), params),

    /**
     * POST Повторить заявку на экспорт (retry)
     * @param _requestId_ — ID заявки
     */
    financeExportRequestRetry: (params: { _requestId_: number | string }) =>
        buildUrl(getRoute('financeExportRequestRetry'), params),

    // ─── Payments & Accounts ─────────────────────────────────────────────────

    /** GET Список счетов компании */
    getAccountList: () => getRoute('getAccountList'),

    /** POST Добавить счёт компании */
    addAccount: () => getRoute('addAccount'),

    /**
     * POST Архивировать счёт
     * @param _guid_ — GUID счёта
     */
    archiveAccount: (params: { _guid_: string }) => buildUrl(getRoute('archiveAccount'), params),

    /**
     * POST Разархивировать счёт
     * @param _guid_ — GUID счёта
     */
    unArchiveAccount: (params: { _guid_: string }) =>
        buildUrl(getRoute('unArchiveAccount'), params),

    /**
     * GET Получить заявку на оплату
     * @param _guid_ — GUID заявки
     */
    getPaymentRequestForReact: (params: { _guid_: string }) =>
        buildUrl(getRoute('getPaymentRequestForReact'), params),

    /**
     * POST Обновить статус заявки на оплату
     * @param _guid_ — GUID заявки
     */
    paymentRequestStatus: (params: { _guid_: string }) =>
        buildUrl(getRoute('paymentRequestStatus'), params),

    /** GET Список партнёров для платежей */
    getPartnerForPayments: () => getRoute('getPartnerForPayments'),

    /** GET Список статей расходов */
    getExpenditure: () => getRoute('getExpenditure'),

    /** GET Список смет для привязки платежей */
    getEstimateForPayments: () => getRoute('getEstimateForPayments'),

    /** POST Добавить приходный платёж */
    addDebitPaymentForReact: () => getRoute('addDebitPaymentForReact'),

    /** POST Добавить расходный платёж */
    addCreditPaymentForReact: () => getRoute('addCreditPaymentForReact'),

    /** POST Добавить перевод между счетами */
    addTransferPaymentForReact: () => getRoute('addTransferPaymentForReact'),

    /** POST Интеграционный перевод (Сбербанк и т.п.) */
    companyPaymentIntegrationTransferReact: () =>
        getRoute('companyPaymentIntegrationTransferReact'),

    /** GET Поиск платёжных операций */
    searchCompanyPaymentOperationsReact: () => getRoute('searchCompanyPaymentOperationsReact'),

    /** GET Экспорт платежей в Excel */
    exportCompanyPaymentsReact: () => getRoute('exportCompanyPaymentsReact'),

    /** GET Страница счетов и статей компании */
    viewCompanyAccounts: () => getRoute('viewCompanyAccounts'),

    /** POST Импорт платежей из банковской выписки */
    importCompanyPaymentsReact: () => getRoute('importCompanyPaymentsReact'),

    /** GET Список доступных парсеров банковских выписок */
    getBankPaymentParsers: () => getRoute('getBankPaymentParsers'),

    /** GET Список интеграций компании */
    getIntegrationListByCompany: () => getRoute('getIntegrationListByCompany'),

    /** POST Синхронизировать данные из интеграции */
    syncIntegration: () => getRoute('syncIntegration'),

    /**
     * GET Список счетов по типу интеграции (Сбербанк, Тинькофф и т.п.)
     * @param _integrationType_ — тип интеграции
     */
    getIntegrationAccountListByCompany: (params: { _integrationType_: string }) =>
        buildUrl(getRoute('getIntegrationAccountListByCompany'), params),

    /** POST Создать нового контрагента */
    createCompanyPartner: () => getRoute('createCompanyPartner'),

    /** POST Загрузить реквизиты компании через DaData */
    loadRequisites: () => getRoute('loadRequisites'),

    // ─── Currency ────────────────────────────────────────────────────────────

    /** GET Список доступных валют */
    getCurrencyList: () => getRoute('getCurrencyList'),

    // ─── Notifications ────────────────────────────────────────────────────────

    /** GET Список уведомлений пользователя */
    notificationsList: () => getRoute('notificationsList'),

    /** POST Отметить уведомление как просмотренное */
    notificationsSetIsViewed: () => getRoute('notificationsSetIsViewed'),

    /** GET Есть ли непрочитанные уведомления */
    notificationsHasUnreadNotifications: () => getRoute('notificationsHasUnreadNotifications'),

    /** POST Отметить все уведомления как просмотренные */
    notificationsSetIsViewedAll: () => getRoute('notificationsSetIsViewedAll'),

    // ─── Tasks ────────────────────────────────────────────────────────────────

    /** POST Создать задачу */
    createTask: () => getRoute('createTask'),

    // ─── Widgets & Tariffs ────────────────────────────────────────────────────

    /** GET Список всех виджетов */
    getWidgetsList: () => getRoute('getWidgetsList'),

    /**
     * GET Планы виджета для покупки
     * @param _widgetId_ — ID виджета
     */
    getWidgetPlanData: (params: { _widgetId_: number | string }) =>
        buildUrl(getRoute('getWidgetPlanData'), params),

    /**
     * POST Оплатить виджет картой
     * @param _widgetPlanId_ — ID плана виджета
     */
    payWidgetByCard: (params: { _widgetPlanId_: number | string }) =>
        buildUrl(getRoute('payWidgetByCard'), params),

    /**
     * POST Подключить бесплатный виджет
     * @param _widgetId_ — ID виджета
     */
    addFreeWidget: (params: { _widgetId_: number | string }) =>
        buildUrl(getRoute('addFreeWidget'), params),

    /**
     * GET Информация о виджете
     * @param _widgetId_ — ID виджета
     */
    getWidgetInfo: (params: { _widgetId_: number | string }) =>
        buildUrl(getRoute('getWidgetInfo'), params),

    /**
     * GET Попап смены тарифного плана
     * @param _prolong_ — '1' если продление, '0' если новая подписка
     */
    openChangeTariffPopup: (params: { _prolong_: string | number }) =>
        buildUrl(getRoute('openChangeTariffPopup'), params),

    /**
     * GET Попап смены тарифа, открытый из конкретного виджета
     * @param _widgetId_ — ID виджета
     */
    openChangeTariffPopupByWidget: (params: { _widgetId_: number | string }) =>
        buildUrl(getRoute('openChangeTariffPopupByWidget'), params),

    // ─── Purchase / Orders Recognition ───────────────────────────────────────

    /** GET Список распознанных закупочных документов */
    getOrdersRecognitionList: () => getRoute('getOrdersRecognitionList'),

    /** POST Загрузить документ для AI-распознавания */
    ordersRecognitionUpload: () => getRoute('ordersRecognitionUpload'),

    /**
     * DELETE Удалить распознанный документ
     * @param _purchaseDocumentId_ — ID документа
     */
    ordersRecognitionDelete: (params: { _purchaseDocumentId_: number | string }) =>
        buildUrl(getRoute('ordersRecognitionDelete'), params),

    /** GET Поиск по закупочным документам */
    ordersRecognitionSearch: () => getRoute('ordersRecognitionSearch'),

    /** POST Отметить позицию документа как использованную в смете */
    ordersRecognitionSetItemProceed: () => getRoute('ordersRecognitionSetItemProceed'),

    /**
     * POST Сохранить комментарий к позиции заказа
     * @param _guid_ — GUID позиции
     */
    saveOrderCommentUrl: (params: { _guid_: string }) =>
        buildUrl(getRoute('saveOrderCommentUrl'), params),

    /**
     * GET График закупок по смете
     * @param _estimateId_ — ID сметы
     */
    getPurchaseChartInfo: (params: { _estimateId_: number | string }) =>
        buildUrl(getRoute('getPurchaseChartInfo'), params),

    // ─── Polls ────────────────────────────────────────────────────────────────

    /** GET Нужно ли показывать опрос пользователю */
    getEnablePoll: () => getRoute('getEnablePoll'),

    /** POST Сохранить ответ пользователя на опрос */
    savePollResult: () => getRoute('savePollResult'),

    // ─── Company Equipment ───────────────────────────────────────────────────

    /** GET Данные складов и инвентаря компании */
    loadEquipmentData: () => getRoute('loadEquipmentData'),

    /** GET Проекты, к которым можно прикрепить склад */
    availableForAttachmentProjectList: () => getRoute('availableForAttachmentProjectList'),

    /** GET Список складов компании */
    warehouseList: () => getRoute('warehouseList'),

    /** POST Создать склад */
    createWarehouse: () => getRoute('createWarehouse'),

    /** POST Переименовать склад */
    renameWarehouse: (params: { _warehouse_id_: number | string }) =>
        buildUrl(getRoute('renameWarehouse'), params),

    /** DELETE Удалить склад */
    deleteWarehouse: (params: { _warehouse_id_: number | string }) =>
        buildUrl(getRoute('deleteWarehouse'), params),

    /** GET Суммарная информация по складу */
    getWarehouseTotalInfo: (params: { _warehouse_id_: number | string }) =>
        buildUrl(getRoute('getWarehouseTotalInfo'), params),

    /** POST Создать инвентарь */
    createTool: (params: { _warehouse_id_: number | string }) =>
        buildUrl(getRoute('createTool'), params),

    /** POST Обновить статус инвентаря */
    updateToolStatus: (params: { _guid_: string }) =>
        buildUrl(getRoute('updateToolStatus'), params),

    /** DELETE Удалить инвентарь */
    removeTool: (params: { _guid_: string }) => buildUrl(getRoute('removeTool'), params),

    /** GET Данные инвентаря */
    viewInventory: (params: { _guid_: string }) => buildUrl(getRoute('viewInventory'), params),

    /** GET История инвентаря */
    loadInventoryLogs: (params: { _guid_: string }) =>
        buildUrl(getRoute('loadInventoryLogs'), params),

    /** POST Обновить данные инвентаря */
    updateInventory: (params: { _guid_: string }) => buildUrl(getRoute('updateInventory'), params),

    /** POST Обновить дату освобождения инвентаря */
    updateInventoryFreeDate: (params: { _guid_: string }) =>
        buildUrl(getRoute('updateInventoryFreeDate'), params),

    /** POST Загрузить фото инвентаря */
    uploadInventoryPhotos: (params: { _guid_: string }) =>
        buildUrl(getRoute('uploadInventoryPhotos'), params),

    /** GET Суммарная информация по инвентарю компании */
    getTotalInfo: () => getRoute('getTotalInfo'),

    /** GET Экспорт оборудования */
    exportEquipment: () => getRoute('exportEquipment'),

    /** POST Скопировать инвентарь */
    copyInventory: (params: { _guid_: string }) => buildUrl(getRoute('copyInventory'), params),

    /** POST Частично переместить инвентарь */
    partiallyMoveInventory: (params: { _guid_: string }) =>
        buildUrl(getRoute('partiallyMoveInventory'), params),

    // ─── Onboarding Scenarios ─────────────────────────────────────────────────

    /**
     * POST Запустить сценарий онбординга
     * @param _key_ — ключ сценария
     */
    startScenario: (params: { _key_: string }) => buildUrl(getRoute('startScenario'), params),

    /** POST Завершить активный сценарий онбординга */
    finishScenario: () => getRoute('finishScenario'),

    /** POST Отменить активный сценарий онбординга */
    cancelActiveScenario: () => getRoute('cancelActiveScenario'),

    // ─── Demo ─────────────────────────────────────────────────────────────────

    /** POST Поставить в очередь задачу создания демо-аккаунта */
    addDemoCreationTaskToQueue: () => getRoute('addDemoCreationTaskToQueue'),

    /** GET Проверить прогресс создания демо-аккаунта */
    checkDemoCreatingProcess: () => getRoute('checkDemoCreatingProcess'),

    // ─── Admin: Communication ─────────────────────────────────────────────────
    // Маршруты доступны только администраторам (admin panel)

    /** POST Создать категорию коммуникации */
    adminCommunicationCategoryCreate: () => getRoute('adminCommunicationCategoryCreate'),

    /** GET Список категорий коммуникации */
    adminCommunicationCategoryList: () => getRoute('adminCommunicationCategoryList'),

    /** PUT Обновить категорию коммуникации */
    adminCommunicationCategoryUpdate: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationCategoryUpdate'), params),

    /** DELETE Удалить категорию коммуникации */
    adminCommunicationCategoryDelete: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationCategoryDelete'), params),

    /** POST Поднять категорию коммуникации вверх */
    adminCommunicationCategoryUp: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationCategoryUp'), params),

    /** POST Опустить категорию коммуникации вниз */
    adminCommunicationCategoryDown: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationCategoryDown'), params),

    /** POST Создать элемент коммуникации */
    adminCommunicationCreate: () => getRoute('adminCommunicationCreate'),

    /** DELETE Удалить элемент коммуникации */
    adminCommunicationItemDelete: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemDelete'), params),

    /** POST Дублировать элемент коммуникации */
    adminCommunicationItemDuplicate: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemDuplicate'), params),

    /** POST Поднять элемент коммуникации вверх */
    adminCommunicationItemUp: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemUp'), params),

    /** POST Опустить элемент коммуникации вниз */
    adminCommunicationItemDown: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemDown'), params),

    /** PUT Обновить элемент коммуникации */
    adminCommunicationItemUpdate: (params: { _id_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemUpdate'), params),

    /**
     * GET Отчёт по пользователям для элемента коммуникации
     * @param _itemId_ — ID элемента
     */
    adminCommunicationItemUsersReport: (params: { _itemId_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemUsersReport'), params),

    /**
     * POST Добавить получателей к элементу коммуникации
     * @param _itemId_ — ID элемента
     */
    adminCommunicationAddRecipients: (params: { _itemId_: number | string }) =>
        buildUrl(getRoute('adminCommunicationAddRecipients'), params),

    /**
     * GET Экспорт отчёта по пользователям
     * @param _itemId_ — ID элемента
     */
    adminCommunicationItemUsersReportExport: (params: { _itemId_: number | string }) =>
        buildUrl(getRoute('adminCommunicationItemUsersReportExport'), params),

    // ─── Client-side Communication Actions ───────────────────────────────────
    // Действия пользователей в панелях коммуникации (не admin)

    /**
     * POST Свернуть элемент коммуникации
     * @param _itemId_ — ID элемента
     */
    apiCommunicationCollapseItem: (params: { _itemId_: number | string }) =>
        buildUrl(getRoute('apiCommunicationCollapseItem'), params),

    /**
     * POST Закрыть баннер коммуникации
     * @param _itemId_ — ID элемента
     */
    apiCommunicationCloseBannerItem: (params: { _itemId_: number | string }) =>
        buildUrl(getRoute('apiCommunicationCloseBannerItem'), params),

    /**
     * POST Выполнить действие в коммуникации (отметить как выполненное)
     * @param _actionId_ — ID действия
     */
    apiCommunicationDoneAction: (params: { _actionId_: number | string }) =>
        buildUrl(getRoute('apiCommunicationDoneAction'), params),
    /**
     * GET Получить все комменты по позиции
     * @param _guid_ — ID позиции
     */
    getCommentListUrl: (params: { _guid_: string }) =>
        buildUrl(getRoute('getCommentListUrl'), params),
    /**
     * GET Получить все комменты по позиции сверхбюджета
     * @param _positionId_ — ID позиции
     */
    getExcessCommentListUrl: (params: { _positionId_: string }) =>
        buildUrl(getRoute('getExcessCommentListUrl'), params),
    /**
     * GET Получить все комменты по позиции в сверх-работах
     * @param _id_ — ID позиции
     */
    getWorkCommentListUrl: (params: { _id_: string }) =>
        buildUrl(getRoute('getExtraWorkCommentListUrl'), params),
    /**
     * POST Добавить коммент по позиции
     * @param _guid_ — ID позиции
     */
    saveCommentUrl: (params: { _guid_: string }) => buildUrl(getRoute('saveCommentUrl'), params),
    /**
     * POST Добавить коммент по позиции сверхбюджета
     * @param _guid_ — ID позиции
     */
    saveExcessCommentUrl: (params: { _excessId_: string }) =>
        buildUrl(getRoute('saveExcessCommentUrl'), params),
    /**
     * POST Добавить коммент по позиции сверх-работах
     * @param _guid_ — ID позиции
     */
    saveExtraWorkCommentUrl: (params: { _id_: string }) =>
        buildUrl(getRoute('saveExtraWorkCommentUrl'), params),
    /**
     * POST Удалить коммент по позиции
     * @param _guid_ — ID позиции
     */
    deleteCommentUrl: (params: { _guid_: string; _commentId_: string }) =>
        buildUrl(getRoute('deleteCommentUrl'), params),
    /**
     * POST Удалить коммент по позиции сверх-бюджета
     * @param _guid_ — ID позиции
     */
    deleteExcessCommentListUrl: (params: { _positionId_: string; _commentId_: string }) =>
        buildUrl(getRoute('deleteExcessCommentListUrl'), params),
    /**
     * POST Удалить коммент по позиции сверх-работ
     * @param _guid_ — ID позиции
     */
    deleteExtraWorkCommentUrl: (params: { _positionExtraWorkId_: string; _commentId_: string }) =>
        buildUrl(getRoute('deleteExtraWorkCommentUrl'), params),
    /**
     * GET получение списка позиций сметы, доступных для создания заявки на снабжение.
     */
    getEstimateSupplyRequestPositionList: () => getRoute('getEstimateSupplyRequestPositionList'),
    /**
     * POST Создаёт заявку на снабжение по позициям сметы и/или внесметным позициям.
     */
    createEstimateSupplyRequest: () => getRoute('createEstimateSupplyRequest'),
    /**
     * GET Получит список непрочитанных гуидов позиций с непрочитанными сообщениями
     * @param _guid_ — ID позиции
     */
    getUnreadCommentsUrl: (params: { _estimateId_: string }) =>
        buildUrl(getRoute('getUnreadCommentGuidsUrl'), params),
    /**
     * GET Получит список непрочитанных гуидов позиций с непрочитанными сообщениями в закупках сверхбюджета
     * @param _estimateId_ — ID сметы
     */
    getUnreadExcessCommentListUrl: (params: { _estimateId_: string }) =>
        buildUrl(getRoute('getUnreadExcessCommentListUrl'), params),
    /**
     * GET Получит список непрочитанных гуидов позиций с непрочитанными сообщениями в работах сверхбюджета
     * @param _estimateId_ — ID сметы
     */
    getUnreadWorkCommentListUrl: (params: { _estimateId_: string }) =>
        buildUrl(getRoute('getUnreadExtraWorkCommentListUrl'), params),
} as const;
